"use client";

import { usePrivy } from "@privy-io/react-auth";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { cn } from "logic/utils";
import { type ReactNode, useEffect, useState } from "react";

import { EASE } from "./constants";
import { EmailLogin } from "./EmailLogin";
import { WalletLogin } from "./WalletLogin";

const CONTACT_URL = "https://usd.ai/contact";
const ARTWORK = "/images/login/check-your-balance.webp";

/** What a borrower comes here to do; the artwork's line cycles through them. */
const TASKS = ["Check your balance", "Pay your loan", "Track your payments"];
const TASK_MS = 3200;

/** As the usd.ai footer has them (taupe tiles, ink glyphs, cream on hover), at
    the mock's 34px, with the USD.AI mark last, linking home. */
const SOCIALS = [
  { label: "USD.AI on X", href: "https://x.com/USDai_Official", Icon: XIcon },
  { label: "USD.AI on LinkedIn", href: "https://www.linkedin.com/company/usd-ai/", Icon: LinkedInIcon },
  { label: "USD.AI on Telegram", href: "https://t.me/usdaiofficial", Icon: TelegramIcon },
  { label: "USD.AI events", href: "https://luma.com/usdai?period=past", Icon: CalendarIcon },
  { label: "USD.AI website", href: "https://usd.ai", Icon: UsdaiIcon },
];

/**
 * The borrower login splash. The artwork panel sits on the left from `lg`; the
 * three ways in run down the right, from email at the top to "Not sure?" and
 * the socials, flush with the artwork's foot. The email field becomes the
 * passcode field once a code is requested, in the same place. It stands alone,
 * without the app shell. Users already signed in by email only need a wallet,
 * so they get the wallet half alone.
 */
export function BorrowerLogin() {
  const { user } = usePrivy();
  const showEmail = !user?.email;

  return (
    <div className="flex min-h-dvh flex-col bg-surface-surface lg:h-dvh lg:min-h-[640px] lg:flex-row lg:gap-14 lg:p-6">
      <Artwork />

      <div className="flex flex-1 px-6 py-12 lg:px-0 lg:py-0">
        <div className="mx-auto flex w-full max-w-[520px] flex-col gap-12 lg:mx-0 lg:justify-between lg:gap-0 lg:pt-[46px]">
          <div className="flex flex-col">
            <Reveal delay={0.2}>
              <h1 className="font-eiko text-[34px] leading-[1.2] text-surface-on-surface sm:text-[40px]">
                USD.AI Borrower Login
              </h1>
            </Reveal>

            {showEmail ? (
              <>
                <Reveal className="mt-12 max-w-[467px] lg:mt-16" delay={0.28}>
                  <Section title="Email Login">
                    <EmailLogin />
                  </Section>
                </Reveal>

                <Reveal delay={0.34}>
                  <hr className="mt-3 mb-9 border-0 border-t border-sys-outline-outline" />
                </Reveal>
              </>
            ) : null}

            <Reveal className={cn({ "mt-12 lg:mt-16": !showEmail })} delay={0.4}>
              <Section title="Wallet Login">
                <WalletLogin />
              </Section>
            </Reveal>
          </div>

          <Reveal delay={0.48}>
            <Section title="Not Sure?" tight>
              <a
                className="text-body-lg-400 w-fit text-surface-on-surface-variant underline-offset-4 transition-colors hover:text-surface-on-surface hover:underline"
                href={CONTACT_URL}
              >
                Schedule a meeting with USD.AI
              </a>
              <Socials />
            </Section>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

/** Fades and lifts its content in on load, after `delay` seconds. */
function Reveal(props: { delay: number; children: ReactNode; className?: string }) {
  const { delay, children, className } = props;
  const reduced = useReducedMotion();

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className={className}
      initial={reduced ? false : { opacity: 0, y: 14 }}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/** The painting, settling in from a slight zoom, with the glyph and the
 *  rotating line over it. */
function Artwork() {
  const reduced = useReducedMotion();

  return (
    <motion.div
      animate={{ opacity: 1 }}
      className="relative h-72 shrink-0 overflow-hidden bg-black sm:h-96 lg:h-auto lg:w-[51%] lg:rounded-lg"
      initial={reduced ? false : { opacity: 0 }}
      transition={{ duration: 0.6, ease: EASE }}
    >
      <motion.img
        alt=""
        animate={{ scale: 1 }}
        className="absolute inset-0 size-full object-cover object-[60%_50%]"
        initial={reduced ? false : { scale: 1.06 }}
        src={ARTWORK}
        transition={{ duration: 1.6, ease: EASE }}
      />
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-linear-to-t from-black/45 to-transparent" />

      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="absolute bottom-8 left-6 flex flex-col gap-6 sm:bottom-12 sm:left-10 lg:bottom-16 lg:left-[70px]"
        initial={reduced ? false : { opacity: 0, y: 16 }}
        transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
      >
        <Glyph className="size-11.5" />
        <RotatingTask />
      </motion.div>
    </motion.div>
  );
}

/** Cycles through `TASKS`, each line rising in as the last one lifts away.
 *  Holds the first under reduced motion. */
function RotatingTask() {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduced) return;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % TASKS.length), TASK_MS);
    return () => window.clearInterval(timer);
  }, [reduced]);

  return (
    // Sized by the longest line, so the glyph above never moves as they change.
    <p className="relative font-eiko text-[32px] leading-[1.15] text-white sm:text-[52px]">
      <span aria-hidden className="invisible">
        {TASKS.reduce((a, b) => (b.length > a.length ? b : a))}
      </span>
      <AnimatePresence initial={false}>
        <motion.span
          key={TASKS[index]}
          animate={{ opacity: 1, y: "0%", filter: "blur(0px)" }}
          className="absolute inset-0 whitespace-nowrap"
          exit={{ opacity: 0, y: "-35%", filter: "blur(6px)" }}
          initial={{ opacity: 0, y: "35%", filter: "blur(6px)" }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          {TASKS[index]}
        </motion.span>
      </AnimatePresence>
    </p>
  );
}

function Section(props: { title: string; children: ReactNode; tight?: boolean }) {
  const { title, children, tight } = props;

  return (
    <section className={cn("flex flex-col", tight ? "gap-3" : "gap-5")}>
      <h2 className="text-display-sm-400 text-surface-on-surface">{title}</h2>
      {children}
    </section>
  );
}

function Socials() {
  return (
    <ul aria-label="Social links" className="mt-4 flex gap-1.5">
      {SOCIALS.map(({ label, href, Icon }) => (
        <li key={label}>
          <a
            aria-label={label}
            className="flex size-[34px] items-center justify-center rounded-[3px] bg-[#CCBBA9] text-[#181614] transition-colors duration-200 hover:bg-[#F7F2EB] active:scale-[0.98]"
            href={href}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Icon />
          </a>
        </li>
      ))}
    </ul>
  );
}

/** The USD.AI glyph in its outlined square, in cream (the brand kit's "Glyph Alternate"). */
function Glyph(props: { className?: string }) {
  return (
    <svg aria-hidden className={props.className} fill="none" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <rect height="97.2973" rx="4.531" stroke="#F7F2EB" strokeWidth="2.7027" width="97.2973" x="1.35135" y="1.35135" />
      <path
        d="M81.8081 40.7442L80.1677 43.0407L78.5274 45.336L75.2467 49.9289L73.6075 52.2254L70.8823 50.61L68.1571 48.9945L65.4318 47.3802L62.7066 45.7647L59.9825 44.1493L57.2573 42.535L54.5321 40.9195L51.8069 39.3041L51.084 38.8753V22.5342L51.8329 22.9776L54.5581 24.5931L57.2822 26.2085L60.0074 27.8229L62.7326 29.4383L65.4579 31.0538L68.1831 32.6692L70.9072 34.2835L73.6324 35.899L76.3576 37.5144L79.0828 39.1287L81.8081 40.7442Z"
        fill="#F7F2EB"
      />
      <path
        d="M71.9672 54.5207L67.0462 61.4102L65.407 63.7055L63.7666 66.002L61.0414 64.3865L58.3162 62.7722L55.591 61.1568L52.8657 59.5413L51.084 58.4858V42.1436L52.8917 43.216L55.617 44.8303L58.3422 46.4458L61.0674 48.0612L63.7915 49.6755L66.5167 51.291L69.2419 52.9064L71.9672 54.5207Z"
        fill="#F7F2EB"
      />
      <path
        d="M62.1263 68.2983L60.4859 70.5948L58.8456 72.8901L57.2053 75.1866L55.5661 77.483L52.8408 75.8676L51.084 74.8268V61.7539L51.2265 61.8376L53.9506 63.4531L56.6758 65.0674L59.4011 66.6828L62.1263 68.2983Z"
        fill="#F7F2EB"
      />
      <path
        d="M18.0996 40.7442L19.7399 43.0407L21.3803 45.336L24.661 49.9289L26.3002 52.2254L29.0254 50.61L31.7506 48.9945L34.4758 47.3802L37.2011 45.7647L39.9251 44.1493L42.6504 42.535L45.3756 40.9195L48.1008 39.3041L48.8237 38.8753V22.5342L48.0748 22.9776L45.3496 24.5931L42.6255 26.2085L39.9003 27.8229L37.175 29.4383L34.4498 31.0538L31.7246 32.6692L29.0005 34.2835L26.2753 35.899L23.5501 37.5144L20.8248 39.1287L18.0996 40.7442Z"
        fill="#F7F2EB"
      />
      <path
        d="M27.9453 54.5207L32.8663 61.4102L34.5055 63.7055L36.1459 66.002L38.8711 64.3865L41.5963 62.7722L44.3215 61.1568L47.0468 59.5413L48.8285 58.4858V42.1436L47.0207 43.216L44.2955 44.8303L41.5703 46.4458L38.8451 48.0612L36.121 49.6755L33.3958 51.291L30.6705 52.9064L27.9453 54.5207Z"
        fill="#F7F2EB"
      />
      <path
        d="M37.7852 68.2983L39.4255 70.5948L41.0658 72.8901L42.7062 75.1866L44.3454 77.483L47.0706 75.8676L48.8275 74.8268V61.7539L48.6849 61.8376L45.9608 63.4531L43.2356 65.0674L40.5104 66.6828L37.7852 68.2983Z"
        fill="#F7F2EB"
      />
    </svg>
  );
}

// The usd.ai footer's social glyphs, as drawn there.
function XIcon() {
  return (
    <svg aria-hidden fill="currentColor" height="14" viewBox="0 0 24 24" width="14">
      <path d="M18.244 2H21.5l-7.5 8.57L22.8 22h-6.9l-5.4-7.06L4.3 22H1.04l8.02-9.17L.6 2h7.07l4.88 6.45L18.24 2Zm-1.14 18.1h1.8L7.02 3.8H5.08l12.02 16.3Z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg aria-hidden fill="none" height="14" viewBox="0 0 24 24" width="14">
      <rect height="19" rx="2" stroke="currentColor" strokeWidth="1.6" width="19" x="2.5" y="2.5" />
      <path
        d="M7.2 10.2v7M7.2 7.2v.1M11 17.2v-4.1c0-1.5 1-2.6 2.5-2.6s2.5 1.1 2.5 2.6v4.1M11 10.2v7"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function TelegramIcon() {
  return (
    <svg aria-hidden fill="none" height="15" viewBox="0 0 24 24" width="15">
      <path
        d="M21 3 3.6 10.2c-.8.3-.8 1.4 0 1.7l4.5 1.6 1.7 5.2c.2.7 1.1.9 1.6.3l2.4-2.6 4.6 3.4c.6.4 1.4.1 1.6-.6L23 4.2c.2-.8-.7-1.5-1.4-1.2H21Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
      <path d="m8.1 13.5 11.4-8.6" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg aria-hidden fill="none" height="14" viewBox="0 0 24 24" width="14">
      <rect height="17" rx="2" stroke="currentColor" strokeWidth="1.6" width="19" x="2.5" y="4.5" />
      <path d="M2.5 9.5h19M7.5 2.5v4M16.5 2.5v4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6" />
      <path
        d="M10.4 13.2h2.2v5.6M10.2 18.8h4.6"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  );
}

/** The USD.AI mark's six chevrons, in the tile's ink. */
function UsdaiIcon() {
  return (
    <svg aria-hidden fill="currentColor" height="16" viewBox="33 38 99 89" width="18">
      <path d="M33.5156 67.8227L46.6348 86.1903L82.6678 64.8329V38.6904L33.5156 67.8227Z" />
      <path d="M49.259 89.8623L62.3782 108.23L82.6678 96.2056V70.0614L49.259 89.8623Z" />
      <path d="M65.0025 111.904L75.4975 126.598L82.6679 122.348V101.434L82.4399 101.568L65.0025 111.904Z" />
      <path d="M131.82 67.8227L118.701 86.1903L82.668 64.8329V38.6904L131.82 67.8227Z" />
      <path d="M116.077 89.8623L102.958 108.23L82.668 96.2056V70.0614L116.077 89.8623Z" />
      <path d="M100.333 111.904L89.8384 126.598L82.668 122.348V101.434L82.896 101.568L100.333 111.904Z" />
    </svg>
  );
}
